import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '../configuration/tokenHandling/prepareHeaders';

export type FleetGroupTag = {
    id: string;
    name: string;
};

type TagsApiLink = {
    href?: string;
};

type TagsApiPage = {
    items?: FleetGroupTag[];
    _links?: {
        next?: TagsApiLink;
    };
};

const tagsApiBaseUrl = import.meta.env.VITE_TAGS_API;

const normalizeTagsApiUrl = (url: string) => {
    try {
        return new URL(url, tagsApiBaseUrl).href;
    } catch {
        return url;
    }
};

export const tagsApi = createApi({
    reducerPath: 'tagsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: tagsApiBaseUrl,
        prepareHeaders,
    }),
    endpoints: builder => ({
        getFleetGroupTags: builder.query<FleetGroupTag[], string | undefined>({
            async queryFn(accountId, _api, _extraOptions, fetchWithBQ) {
                const items: FleetGroupTag[] = [];
                const visitedNextHrefs = new Set<string>();
                let url = accountId ? `/tags?account_id=${encodeURIComponent(accountId)}` : '/tags';

                while (url) {
                    const result = await fetchWithBQ(url);

                    if (result.error) {
                        return { error: result.error };
                    }

                    const page = result.data as TagsApiPage;
                    items.push(...(page.items ?? []));

                    const nextHref = page._links?.next?.href;
                    if (!nextHref) {
                        break;
                    }

                    const lastCalledHref = normalizeTagsApiUrl(url);
                    const normalizedNextHref = normalizeTagsApiUrl(nextHref);
                    if (normalizedNextHref === lastCalledHref || visitedNextHrefs.has(normalizedNextHref)) {
                        break;
                    }

                    visitedNextHrefs.add(normalizedNextHref);
                    url = nextHref;
                }

                return { data: items };
            },
        }),
    }),
});

export const { useGetFleetGroupTagsQuery } = tagsApi;
